import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Platform } from '@ionic/angular/standalone';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Attachment } from 'src/app/models/item.model';
import { ImageSelectedEventPayload } from '../../models/event-payloads.model';
import { SecurePipe, placeholder } from '../secure.pipe';

export interface ImageDetail {
  id: string;
  thumbUrl?: string | ArrayBuffer;
  fullUrl?: string | ArrayBuffer;
  loadingFull?: boolean;
}

@Component({
  selector: 'app-attachment-image-column',
  templateUrl: './attachment-image-column.component.html',
  styleUrls: ['./attachment-image-column.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, AsyncPipe]
})
export class AttachmentImageColumnComponent implements OnInit, OnChanges {
  @Input() attachments: Attachment[];
  @Output() imageSelected = new EventEmitter<ImageSelectedEventPayload>();
  @Output() getThumb = new EventEmitter<string>();
  @Output() getFull = new EventEmitter<string>();
  imagesSubject = new BehaviorSubject<ImageDetail[]>([]);
  images$: Observable<ImageDetail[]> = this.imagesSubject.asObservable();

  imageMap = new Map<string, ImageDetail>();
  private hasFileSystem;
  constructor(
    private securePipe: SecurePipe,
    platform: Platform,
    private domSanitizer: DomSanitizer
  ) {
    this.hasFileSystem = platform.is('hybrid');
  }

  ngOnInit(): void {
    this.processAttachments(this.attachments);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes.attachments;
    if (change && !change.isFirstChange()) {
      this.processAttachments(change.currentValue);
    }
  }

  processAttachments(attachments: Attachment[]): void {
    attachments.forEach((a) => {
      if (this.hasFileSystem) {
        this.processHybridAttachment(a);
      } else {
        this.processBrowserAttachment(a);
      }
    });
    this.update();
  }

  processHybridAttachment(attachment: Attachment): void {
    this.imageMap.set(attachment.attachmentId, {
      ...this.imageMap.get(attachment.attachmentId),
      id: attachment.attachmentId,
      thumbUrl: attachment.thumbUrl,
      fullUrl: attachment.fullUrl
    });
    // We need to check we are not already loading the full image since updates will
    // appear asynchronously and we regenerate the entire list each time
    if (!attachment.fullUrl && !this.imageMap.get(attachment.attachmentId).loadingFull) {
      this.imageMap.get(attachment.attachmentId).loadingFull = true;
      this.getFull.emit(attachment.attachmentId);
    }
  }

  processBrowserAttachment(attachment: Attachment): void {
    this.imageMap.set(attachment.attachmentId, {
      ...this.imageMap.get(attachment.attachmentId),
      id: attachment.attachmentId
    });
    if (!this.imageMap.get(attachment.attachmentId).thumbUrl) {
      this.securePipe
        .transform(attachment.url + '?thumb=true')
        .pipe(
          tap((result) => {
            if (result === placeholder) {
              return;
            }
            this.imageMap.get(attachment.attachmentId).thumbUrl = result;
            this.update();
          })
        )
        .subscribe();
    }
    if (!this.imageMap.get(attachment.attachmentId).fullUrl) {
      // TODO: remove?
    }
    this.securePipe
      .transform(attachment.url)
      .pipe(
        tap((result) => {
          if (result === placeholder) {
            return;
          }
          this.imageMap.get(attachment.attachmentId).fullUrl = result;
          this.update();
        })
      )
      .subscribe();
  }

  update(): void {
    this.imagesSubject.next(Array.from(this.imageMap.values()));
  }

  trackByFn(_: number, item: any): number {
    return item.attachmentId;
  }

  onImageSelected(image: ImageDetail): void {
    if (this.hasFileSystem && (image.fullUrl || image.thumbUrl)) {
      this.imageSelected.emit({ id: image.id });
    } else {
      fetch((image.fullUrl ? image.fullUrl : image.thumbUrl) as string).then(result =>
        result.blob().then(blob => this.imageSelected.emit({ id: image.id, content: blob }))
      );
    }
  }

  fullImageClass(image: ImageDetail): string {
    return image.thumbUrl ? 'underlay' : 'overlay';
  }

  sanitizeUrl(url: string | ArrayBuffer): SafeResourceUrl {
    return this.domSanitizer.bypassSecurityTrustResourceUrl(url as string);
  }
}
